import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// HttpOnly session cookies are issued by the backend on /auth/login,
// /auth/register (admin path), and /checkout/status (success path).
// We just need to make sure all requests include credentials.
export const api = axios.create({ baseURL: API, withCredentials: true });

export const partnersApi = {
  inquiry: (data) => api.post("/partners/inquiry", data).then((r) => r.data),
  /** Invite-only Partner VIP Access: self-serve 7-day evaluation grant. */
  vipAccess: (data) => api.post("/vipaccess", data).then((r) => r.data),
};

export const publicContentApi = {
  partners: () => api.get("/public/partners").then((r) => r.data),
  pressList: () => api.get("/public/press").then((r) => r.data),
  pressDetail: (slug) => api.get(`/public/press/${slug}`).then((r) => r.data),
  emtechStatus: () => api.get("/public/emtech-status").then((r) => r.data),
};

export const adminPressApi = {
  list: () => api.get("/admin/press").then((r) => r.data),
  create: (data) => api.post("/admin/press", data).then((r) => r.data),
  update: (id, data) => api.put(`/admin/press/${id}`, data).then((r) => r.data),
  publish: (id) => api.post(`/admin/press/${id}/publish`).then((r) => r.data),
  unpublish: (id) => api.post(`/admin/press/${id}/unpublish`).then((r) => r.data),
  remove: (id) => api.delete(`/admin/press/${id}`).then((r) => r.data),
  setEmtech: (on) => api.post("/admin/emtech/publish", { on }).then((r) => r.data),
};

export const adminPartnersApi = {
  list: () => api.get("/admin/partners").then((r) => r.data),
  grant: (id) => api.post(`/admin/partners/${id}/grant`).then((r) => r.data),
  update: (id, data) => api.patch(`/admin/partners/${id}`, data).then((r) => r.data),
  resendLink: (id) => api.post(`/admin/partners/${id}/resend-link`).then((r) => r.data),
  extend: (id, newExpiresAt) =>
    api.post(`/admin/partners/${id}/extend`, { new_expires_at: newExpiresAt }).then((r) => r.data),
};

export const adminBannedApi = {
  list: () => api.get("/admin/banned-emails").then((r) => r.data),
  add: (email, reason) => api.post("/admin/banned-emails", { email, reason }).then((r) => r.data),
  remove: (email) => api.delete(`/admin/banned-emails/${encodeURIComponent(email)}`).then((r) => r.data),
};

export const authApi = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  updateProfile: (data) => api.put("/auth/profile", data).then((r) => r.data),
  resumeCheckout: (data) => api.post("/auth/resume-checkout", data).then((r) => r.data),
  forgotPassword: (email) =>
    api
      .post("/auth/forgot-password", { email, origin: window.location.origin })
      .then((r) => r.data),
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  requestMagicLink: (email) =>
    api
      .post("/auth/magic-link", { email, origin: window.location.origin })
      .then((r) => r.data),
  verifyMagicLink: (token) =>
    api.post(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`).then((r) => r.data),
};

export const webinarApi = {
  /** Role picklist, served by the backend so form and stored values agree. */
  roles: () => api.get("/webinar/roles").then((r) => r.data),
  register: (data) => api.post("/webinar/register", data).then((r) => r.data),
};

export const checkoutApi = {
  pricing: () => api.get("/pricing").then((r) => r.data),
  status: (sessionId) => api.get(`/checkout/status/${sessionId}`).then((r) => r.data),
  /** Logged-in purchase of another Single Assessment Pass. Pass an intro
   *  offer token to apply the server-validated introductory price. */
  singlePass: (introToken = null) =>
    api.post("/payments/single-pass/checkout", {
      origin: window.location.origin,
      ...(introToken ? { intro_token: introToken } : {}),
    }).then((r) => r.data),
  /** Logged-in EXPLORE subscription checkout; SOLO 7-day credit auto-applies. */
  upgrade: (plan = "monthly") =>
    api.post("/payments/upgrade/checkout", { origin: window.location.origin, plan }).then((r) => r.data),
  /** Alias used by the SOLO upgrade banner. */
  upgradeCheckout: (plan = "monthly") =>
    api.post("/payments/upgrade/checkout", { origin: window.location.origin, plan }).then((r) => r.data),
  /** Clarity CORE, the primary engine: $25,000/year annual subscription.
   *  Seat-gated on the server; inviteToken unlocks waitlist-invited checkout. */
  coreCheckout: (inviteToken = null) =>
    api.post("/payments/core/checkout", { origin: window.location.origin, invite_token: inviteToken }).then((r) => r.data),
  /** Stripe Customer Portal for subscribers (manage card, cancel). */
  billingPortal: () =>
    api.post("/billing/portal", { origin: window.location.origin }).then((r) => r.data),
};

export const accountApi = {
  /** Signed-in member asks for their account + data to be deleted (manual, no auto-delete). */
  requestDeletion: (reason) =>
    api.post("/account/deletion-request", { reason: reason || null }).then((r) => r.data),
};

export const checklistApi = {
  get: () => api.get("/checklist").then((r) => r.data),
  capture: (data) => api.post("/checklist/lead", data).then((r) => r.data),
};

export const analysesApi = {
  create: (intake) => api.post("/analyses", { intake }).then((r) => r.data),
  list: () => api.get("/analyses").then((r) => r.data),
  get: (id) => api.get(`/analyses/${id}`).then((r) => r.data),
  remove: (id) => api.delete(`/analyses/${id}`).then((r) => r.data),
  /** Job-based generation: submit returns a job id instantly; poll for the result.
   *  Short-lived polls survive edge proxies that close long responses. */
  createJob: (intake) => api.post("/analyses/jobs", { intake }).then((r) => r.data),
  jobStatus: (jobId) => api.get(`/analyses/jobs/${jobId}`).then((r) => r.data),
  archiveCsvUrl: () => `${API}/analyses/export.csv`,
  /** Strategic Issue-Mapping Refresh gateway. */
  refreshEligibility: (id) => api.get(`/analyses/${id}/refresh/eligibility`).then((r) => r.data),
  refreshCheckout: (id) =>
    api.post(`/analyses/${id}/refresh/checkout`, { origin: window.location.origin }).then((r) => r.data),
  refreshRun: (id) => api.post(`/analyses/${id}/refresh/jobs`).then((r) => r.data),
};

export const intakeApi = {
  /** Distinct professional profiles from past intakes (profile prefill). */
  profiles: () => api.get("/intake/profiles").then((r) => r.data),
  /** The account's single unfinished intake, or { draft: null }. */
  getDraft: () => api.get("/intake/draft").then((r) => r.data),
  saveDraft: (step, form) => api.put("/intake/draft", { step, form }).then((r) => r.data),
  discardDraft: () => api.delete("/intake/draft").then((r) => r.data),
  /** Reusable starting points, saved from a finished memo. */
  templates: () => api.get("/intake/templates").then((r) => r.data),
  saveTemplate: (name, analysisId) =>
    api.post("/intake/templates", { name, analysis_id: analysisId }).then((r) => r.data),
  deleteTemplate: (id) => api.delete(`/intake/templates/${id}`).then((r) => r.data),
};

export const authorityApi = {
  directory: () => api.get("/authority-directory").then((r) => r.data),
  /** Source records for the Currency Notice: notes for these intake
   *  jurisdictions plus Cross-border notes (always). */
  notes: (jurisdictions) =>
    api.get("/authority-notes", { params: { jurisdictions: (jurisdictions || []).join(",") } }).then((r) => r.data),
};

/** Complimentary webinar memo: the token in the link is the sole credential. */
export const freeMemoApi = {
  validate: (token) => api.get("/free-memo/validate", { params: { token } }).then((r) => r.data),
  claim: (token) => api.post("/free-memo/claim", { token }).then((r) => r.data),
  signup: (fields) => api.post("/free-memo/signup", fields).then((r) => r.data),
};

export const newsApi = {
  get: () => api.get("/news").then((r) => r.data),
};

export const adminApi = {
  /** Activation system (feature flags, dashboard, dry-run, intro-offer CSV). */
  activationFlags: () => api.get("/admin/activation/flags").then((r) => r.data),
  activationFlagSet: (key, on) => api.post("/admin/activation/flags", { key, on }).then((r) => r.data),
  activationDashboard: () => api.get("/admin/activation/dashboard").then((r) => r.data),
  activationIntroDryRun: (email) => api.post("/admin/activation/intro/dry-run", { email }).then((r) => r.data),
  activationEmailDryRun: (cohort) => api.post("/admin/activation/emails/dry-run", { cohort }).then((r) => r.data),
  activationAudit: (limit = 50) => api.get(`/admin/activation/audit?limit=${limit}`).then((r) => r.data),
  activationTokensCsvUrl: `${API}/admin/activation/tokens.csv`,
  /** Publisher-maintained authority reference notes with source records. */
  authorityNotes: () => api.get("/admin/authority-notes").then((r) => r.data),
  authorityNoteUpdate: (id, fields) => api.put(`/admin/authority-notes/${id}`, fields).then((r) => r.data),
  authorityNoteCreate: (fields) => api.post("/admin/authority-notes", fields).then((r) => r.data),
  authorityNotesImportTemplateUrl: () => `${API}/admin/authority-notes/import/template.csv`,
  authorityNotesImportPreview: (csv_text) => api.post("/admin/authority-notes/import/preview", { csv_text }).then((r) => r.data),
  authorityNotesImportCommit: (csv_text, file_name) => api.post("/admin/authority-notes/import/commit", { csv_text, file_name }).then((r) => r.data),
  authorityNoteImports: () => api.get("/admin/authority-notes/imports").then((r) => r.data),
  authorityNoteImportArchive: (batchId) => api.post(`/admin/authority-notes/imports/${batchId}/archive`).then((r) => r.data),
  authorityNoteVerification: (id, verified) => api.put(`/admin/authority-notes/${id}/verification`, { verified }).then((r) => r.data),
  authorityNotesBulkVerify: (ids) => api.post("/admin/authority-notes/verification/bulk", { ids }).then((r) => r.data),
  authorityDomainApprove: (domain) => api.post("/admin/authority-notes/approved-domains", { domain }).then((r) => r.data),
  authorityDomainRemove: (domain) => api.delete(`/admin/authority-notes/approved-domains/${encodeURIComponent(domain)}`).then((r) => r.data),
  /** Complimentary webinar memo links: preview + confirmed direct send, tracking summary. */
  freeMemoDefaults: () => api.get("/admin/free-memo/defaults").then((r) => r.data),
  freeMemoPreview: (fields) => api.post("/admin/free-memo/send/preview", fields).then((r) => r.data),
  freeMemoSend: (fields) => api.post("/admin/free-memo/send", fields).then((r) => r.data),
  freeMemoSummary: (batchId) => api.get("/admin/free-memo/summary", { params: batchId ? { batch_id: batchId } : {} }).then((r) => r.data),
  freeMemoCampaigns: () => api.get("/admin/free-memo/campaigns").then((r) => r.data),
  freeMemoExportUrl: (batchId) => `${API}/admin/free-memo/export.csv${batchId ? `?batch_id=${encodeURIComponent(batchId)}` : ""}`,
  authorityNoteArchive: (id) => api.post(`/admin/authority-notes/${id}/archive`).then((r) => r.data),
  authorityNotesExportUrl: () => `${API}/admin/authority-notes/export.csv`,
  authorityImportReferences: (batchId) => api.get(`/admin/authority-notes/imports/${batchId}/references`).then((r) => r.data),
  repointPreview: (csv_text) => api.post("/admin/research-sources/repoint/preview", { csv_text }).then((r) => r.data),
  repointApply: (csv_text, file_name) => api.post("/admin/research-sources/repoint/apply", { csv_text, file_name }).then((r) => r.data),
  repointRuns: () => api.get("/admin/research-sources/repoint/runs").then((r) => r.data),
  repointRevert: (runId) => api.post(`/admin/research-sources/repoint/${runId}/revert`).then((r) => r.data),
  sourcesNeedsReview: () => api.get("/admin/research-sources/needs-review").then((r) => r.data),
  sourceClearReview: (id) => api.post(`/admin/research-sources/${id}/clear-review`).then((r) => r.data),
  /** CORE Research Layer — canonical sources, review queue, tracker-domain map. */
  researchSources: (params = {}) => api.get("/admin/research/sources", { params }).then((r) => r.data),
  researchSourceCreate: (fields) => api.post("/admin/research/sources", fields).then((r) => r.data),
  researchSourceUpdate: (id, fields) => api.put(`/admin/research/sources/${id}`, fields).then((r) => r.data),
  researchSourceVerify: (id, verified) => api.put(`/admin/research/sources/${id}/verification`, { verified }).then((r) => r.data),
  researchSourceDelete: (id) => api.delete(`/admin/research/sources/${id}`).then((r) => r.data),
  researchSourceCandidates: (params = {}) => api.get("/admin/research/source-candidates", { params }).then((r) => r.data),
  researchBatchApprovePreview: (groups) => api.post("/admin/research/sources/batch-approve/preview", { groups }).then((r) => r.data),
  researchBatchApproveApply: (groups) => api.post("/admin/research/sources/batch-approve/apply", { groups }).then((r) => r.data),
  researchBatchApproveRuns: () => api.get("/admin/research/sources/batch-approve/runs").then((r) => r.data),
  researchBatchApproveUndo: (runId) => api.post(`/admin/research/sources/batch-approve/${runId}/undo`).then((r) => r.data),
  researchSourcePrefillPreview: (csv_text) => api.post("/admin/research/sources/prefill/preview", { csv_text }).then((r) => r.data),
  researchDomainMap: () => api.get("/admin/research/domain-map").then((r) => r.data),
  researchDomainMapUpsert: (fields) => api.post("/admin/research/domain-map", fields).then((r) => r.data),
  researchDomainMapApprove: (id) => api.put(`/admin/research/domain-map/${id}/approve`).then((r) => r.data),
  researchDomainMapReject: (id) => api.put(`/admin/research/domain-map/${id}/reject`).then((r) => r.data),
  researchDomainMapRestore: (id) => api.put(`/admin/research/domain-map/${id}/restore`).then((r) => r.data),
  researchTemplates: (params = {}) => api.get("/admin/research/templates", { params }).then((r) => r.data),
  researchTemplateCreate: (fields) => api.post("/admin/research/templates", fields).then((r) => r.data),
  researchTemplateUpdate: (id, fields) => api.put(`/admin/research/templates/${id}`, fields).then((r) => r.data),
  researchTemplateStatus: (id, publish) => api.put(`/admin/research/templates/${id}/status`, { publish }).then((r) => r.data),
  researchTemplateDelete: (id) => api.delete(`/admin/research/templates/${id}`).then((r) => r.data),
  researchHoldGet: () => api.get("/admin/research/hold").then((r) => r.data),
  researchHoldSet: (on) => api.post("/admin/research/hold", { on }).then((r) => r.data),
  listLeads: () => api.get("/admin/leads").then((r) => r.data),
  triageInsights: () => api.get("/admin/triage/insights").then((r) => r.data),
  faqSearches: () => api.get("/admin/faq-searches").then((r) => r.data),
  faqFeedback: () => api.get("/admin/faq-feedback").then((r) => r.data),
  listDeletionRequests: () => api.get("/admin/deletion-requests").then((r) => r.data),
  previewDeletionRequest: (id) => api.get(`/admin/deletion-requests/${id}/preview`).then((r) => r.data),
  approveDeletionRequest: (id, note) =>
    api.post(`/admin/deletion-requests/${id}/approve`, { confirm: true, note: note || null }).then((r) => r.data),
  closeDeletionRequest: (id, note) =>
    api.post(`/admin/deletion-requests/${id}/close`, { note }).then((r) => r.data),
  reopenDeletionRequest: (id, note) =>
    api.post(`/admin/deletion-requests/${id}/reopen`, { note: note || null }).then((r) => r.data),
  nudgeInsights: () => api.get("/admin/nudge-insights").then((r) => r.data),
  runNudges: (dryRun = false) =>
    api.post(`/admin/refresh-nudges/send?dry_run=${dryRun}`).then((r) => r.data),
  mailerliteBackfill: () => api.post("/admin/mailerlite/backfill").then((r) => r.data),
  /** Acquisition webhooks: configured, last fired, last outcome. */
  webhookHealth: () => api.get("/admin/webhook-health").then((r) => r.data),
  /** Live session reminders: status, and a dry-run-first manual trigger. */
  webinarReminderStatus: () => api.get("/admin/webinar/reminders").then((r) => r.data),
  /** Registration handoff health: who has not reached the MailerLite group yet. */
  webinarRegistrants: () => api.get("/admin/webinar-registrants").then((r) => r.data),
  webinarHandoffRetry: () =>
    api.post("/admin/webinar/handoff/retry").then((r) => r.data),
  sendWebinarReminders: (sessionDate, dryRun = true) =>
    api.post("/admin/webinar/reminders/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun },
    }).then((r) => r.data),
  /** After-session recap: dry-run-first manual trigger, once per registrant per session. */
  sendWebinarRecap: (sessionDate, dryRun = true) =>
    api.post("/admin/webinar/recap/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun },
    }).then((r) => r.data),
  /** "We're on now": the 11:45 ET room-link send, status and manual trigger. */
  webinarLiveNowStatus: () => api.get("/admin/webinar/live-now").then((r) => r.data),
  sendWebinarLiveNow: (sessionDate, dryRun = true, onlyTo = undefined) =>
    api.post("/admin/webinar/live-now/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun, only_to: onlyTo },
    }).then((r) => r.data),
  /** Replay notes: the founder's per-session recording and worksheet email. */
  replayStatus: () => api.get("/admin/webinar/replays").then((r) => r.data),
  sendReplay: (sessionDate, dryRun = true, onlyTo = undefined) =>
    api.post("/admin/webinar/replay/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun, only_to: onlyTo },
    }).then((r) => r.data),
  /** Strategy Briefing invitation: automatic three days after each replay note. */
  strategyBriefingStatus: () => api.get("/admin/webinar/strategy-briefing").then((r) => r.data),
  sendStrategyBriefing: (sessionDate = undefined, dryRun = true, onlyTo = undefined) =>
    api.post("/admin/webinar/strategy-briefing/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun, only_to: onlyTo },
    }).then((r) => r.data),
  /** Session worksheets: what is on file, and a backfill for earlier registrants. */
  worksheetStatus: () => api.get("/admin/webinar/worksheets").then((r) => r.data),  sendWorksheets: (sessionDate, dryRun = true, onlyTo = undefined) =>
    api.post("/admin/webinar/worksheets/send", null, {
      params: { session_date: sessionDate, dry_run: dryRun, only_to: onlyTo },
    }).then((r) => r.data),
  /** Link Guard: outbound emails blocked for carrying an unbranded domain. */
  emailLinkGuard: () => api.get("/admin/email-link-guard").then((r) => r.data),
  testWebhook: (event) => api.post("/admin/webhook-health/test", { event }).then((r) => r.data),
  revenue: () => api.get("/admin/revenue").then((r) => r.data),
  eventsSummary: () => api.get("/admin/events/summary").then((r) => r.data),
  bridgeStats: () => api.get("/admin/bridge/stats").then((r) => r.data),
  sendDigest: () => api.post("/admin/digest/send").then((r) => r.data),
  strategyBriefings: () => api.get("/admin/strategy/briefings").then((r) => r.data),
  strategyBriefingQueue: () => api.get("/admin/strategy/briefings/queue").then((r) => r.data),
  updateStrategyBriefing: (id, data) => api.patch(`/admin/strategy/briefings/${id}`, data).then((r) => r.data),
  resendLead: (id) => api.post(`/admin/leads/${id}/resend`).then((r) => r.data),
  retryAllFailedLeads: () => api.post("/admin/leads/retry-failed").then((r) => r.data),
  csvUrl: () => `${API}/admin/leads.csv`,
  listReviews: () => api.get("/admin/reviews").then((r) => r.data),
  createReview: (payload) => api.post("/admin/reviews", payload).then((r) => r.data),
  updateReview: (id, payload) => api.put(`/admin/reviews/${id}`, payload).then((r) => r.data),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`).then((r) => r.data),
  /** Member-submitted review queue: nothing is public until approved. */
  listReviewSubmissions: () => api.get("/admin/review-submissions").then((r) => r.data),
  approveReviewSubmission: (id, note) =>
    api.post(`/admin/review-submissions/${id}/approve`, { note: note || null }).then((r) => r.data),
  rejectReviewSubmission: (id, note) =>
    api.post(`/admin/review-submissions/${id}/reject`, { note: note || null }).then((r) => r.data),
  /** Review requests: day-after-memo follow-up + founder-run campaigns. */
  reviewRequestStatus: () => api.get("/admin/reviews/requests/status").then((r) => r.data),
  sendReviewRequests: (audience, dryRun = true) =>
    api
      .post(`/admin/reviews/requests/send?audience=${audience}&dry_run=${dryRun}`)
      .then((r) => r.data),
  /** Advisory Circle Testers: complimentary EXPLORE access management. */
  listTesters: () => api.get("/admin/testers").then((r) => r.data),
  grantTester: (payload) => api.post("/admin/testers", payload).then((r) => r.data),
  revokeTester: (userId) => api.delete(`/admin/testers/${userId}`).then((r) => r.data),
  extendTester: (userId, days = 7) => api.post(`/admin/testers/${userId}/extend`, { days }).then((r) => r.data),
  reinviteTester: (userId) => api.post(`/admin/testers/${userId}/reinvite`).then((r) => r.data),
  sendTesterInvite: (userId) =>
    api.post(`/admin/testers/${userId}/send-invite`, { origin: window.location.origin }).then((r) => r.data),
  sendTesterReminder: (userId) =>
    api.post(`/admin/testers/${userId}/reminder`, { origin: window.location.origin }).then((r) => r.data),
  setTesterReminder: (userId, enabled) =>
    api.patch(`/admin/testers/${userId}/reminder`, { enabled }).then((r) => r.data),
  testersFeedbackCsvUrl: () => `${API}/admin/testers/feedback.csv`,

  /** CLARITY CORE program: rolling seat cap, waitlist, profiles, briefings. */
  coreOverview: () => api.get("/admin/core/overview").then((r) => r.data),
  coreActivate: (email) => api.post("/admin/core/seats/activate", { email }).then((r) => r.data),
  coreRelease: (userId) => api.post("/admin/core/seats/release", { user_id: userId }).then((r) => r.data),
  coreWaitlistInvite: (entryId) => api.post(`/admin/core/waitlist/${entryId}/invite`).then((r) => r.data),
  coreWaitlistEmailInvite: (entryId) => api.post(`/admin/core/waitlist/${entryId}/send-invite-email`).then((r) => r.data),
  coreWaitlistRemove: (entryId) => api.delete(`/admin/core/waitlist/${entryId}`).then((r) => r.data),
  coreMemberWorkspace: (userId) => api.get(`/admin/core/members/${userId}/workspace`).then((r) => r.data),
  coreProfileSave: (userId, data) => api.put(`/admin/core/members/${userId}/profile`, data).then((r) => r.data),
  coreSessionSave: (userId, data) => api.put(`/admin/core/members/${userId}/session`, data).then((r) => r.data),
  coreBriefingCreate: (userId, data) => api.post(`/admin/core/members/${userId}/briefings`, data).then((r) => r.data),
  coreBriefingUpdate: (id, data) => api.put(`/admin/core/briefings/${id}`, data).then((r) => r.data),
  coreBriefingApprove: (id) => api.post(`/admin/core/briefings/${id}/approve`).then((r) => r.data),
  coreBriefingUnapprove: (id) => api.post(`/admin/core/briefings/${id}/unapprove`).then((r) => r.data),
  coreBriefingDelete: (id) => api.delete(`/admin/core/briefings/${id}`),
  coreRequestUpdate: (id, status) => api.patch(`/admin/core/requests/${id}`, { status }).then((r) => r.data),
  compRefresh: (userId, credits = 1) =>
    api.post(`/admin/users/${userId}/comp-refresh`, { credits }).then((r) => r.data),
  /** Manual plan changes: find an account by email, then move it. */
  memberLookup: (email) =>
    api.get("/admin/members/lookup", { params: { email } }).then((r) => r.data),
  /** Read-only roster of permanent and complimentary standing. */
  memberRoster: () => api.get("/admin/members/roster").then((r) => r.data),
  /** Read-only directory of every registered account. */
  memberDirectory: () => api.get("/admin/members/directory").then((r) => r.data),
  /** Direct CSV download URLs (cookie-authenticated, one-click). */
  memberDirectoryCsvUrl: `${API}/admin/members/directory.csv`,
  rosterCsvUrl: `${API}/admin/members/roster.csv`,
  /** Unified read-only per-person lookup by email (leads, webinar, account, pipeline, memos). */
  peopleLookup: (email) =>
    api.get("/admin/people/lookup", { params: { email } }).then((r) => r.data),
  changeMemberPlan: (userId, body) =>
    api.post(`/admin/members/${userId}/plan`, body).then((r) => r.data),
  renameUser: (userId, full_name) =>
    api.put(`/admin/members/${userId}/display-name`, { full_name }).then((r) => r.data),
  bulkRemoveRoster: (body) =>
    api.post("/admin/members/bulk-remove", body).then((r) => r.data),
  /** Unfinished-intake nudges: status and the manual send. */
  draftNudgeStatus: () => api.get("/admin/intake-drafts/nudges").then((r) => r.data),
  sendDraftNudges: (dryRun = true) =>
    api.post("/admin/intake-drafts/nudges/send", null, { params: { dry_run: dryRun } }).then((r) => r.data),
  /** Verified Regulatory Refresh (internal-test-only) + Founder Review queue. */
  verifiedRefreshQueue: (status) =>
    api.get(`/admin/verified-refresh/queue${status ? `?status=${status}` : ""}`).then((r) => r.data),
  verifiedRefreshRun: (payload) =>
    api.post("/admin/verified-refresh/run", payload).then((r) => r.data),
  verifiedRefreshApprove: (id, note) =>
    api.post(`/admin/verified-refresh/${id}/approve`, { note: note || null }).then((r) => r.data),
  verifiedRefreshReject: (id, note) =>
    api.post(`/admin/verified-refresh/${id}/reject`, { note: note || null }).then((r) => r.data),

  /** Beta 3.0 applicants: list + row actions + manual sequence sweep. */
  beta3Applicants: () => api.get("/admin/beta3/applicants").then((r) => r.data),
  beta3Extend: (userId, days) => api.post(`/admin/beta3/${userId}/extend`, { days }).then((r) => r.data),
  beta3Revoke: (userId) => api.post(`/admin/beta3/${userId}/revoke`).then((r) => r.data),
  beta3Reviewed: (userId, reviewed) => api.post(`/admin/beta3/${userId}/reviewed`, { reviewed }).then((r) => r.data),
  beta3Sweep: () => api.post("/admin/beta3/sweep").then((r) => r.data),

  /** /mugenvip landing page: registrations tagged utm_source="mugenvip".
      days: null (all time), 7, or 30. */
  mugenvipSignups: (days) => api.get("/admin/mugenvip/signups", { params: days ? { days } : {} }).then((r) => r.data),
  mugenvipAnalytics: (days) => api.get("/admin/mugenvip/analytics", { params: days ? { days } : {} }).then((r) => r.data),
  mugenvipDigest: (dryRun = true) => api.post(`/admin/mugenvip/digest?dry_run=${dryRun}`).then((r) => r.data),

  /** Referral-fee ledger: attributed payments with gross, Stripe fee, net and
      fee status (pending / payable / paid / reversed). */
  referralSweep: (partner) => api.post("/admin/referral/sweep", null, { params: partner ? { partner } : {} }).then((r) => r.data),
  referralMarkFeePaid: (sessionId, note) => api.post(`/admin/referral/fees/${sessionId}/mark-paid`, { note: note || null }).then((r) => r.data),

  /** /replay page controls: on/off, the replay library (add / extend / remove), views. */
  replay: () => api.get("/admin/replay").then((r) => r.data),
  replayUpdate: (updates) => api.put("/admin/replay", updates).then((r) => r.data),
  replayAddItem: (fields) => api.post("/admin/replay/items", fields).then((r) => r.data),
  replayUpdateItem: (id, fields) => api.put(`/admin/replay/items/${id}`, fields).then((r) => r.data),
  replayRemoveItem: (id) => api.delete(`/admin/replay/items/${id}`).then((r) => r.data),
  /** Per-session worksheet shown/downloaded on the replay page (admin upload overrides the on-disk default). */
  replayUploadWorksheet: (sessionDate, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/admin/replay/worksheet/${sessionDate}`, fd).then((r) => r.data);
  },
  replayDeleteWorksheet: (sessionDate) =>
    api.delete(`/admin/replay/worksheet/${sessionDate}`).then((r) => r.data),

  /** Standing /survey responses, and the CSV export (cookie-authenticated). */
  survey: () => api.get("/admin/survey").then((r) => r.data),
  surveyExportUrl: () => `${API}/admin/survey/export.csv`,
  /** One-click approval of a survey comment for the public social-proof strip.
      Optional light edits: text, display name, attribution line. */
  surveyApprove: (id, edits) => api.post(`/admin/survey/${id}/approve`, edits || {}).then((r) => r.data),
  surveyUnapprove: (id) => api.post(`/admin/survey/${id}/unapprove`).then((r) => r.data),

  /** /start paid-ad landing page: signups + leads tagged utm_source="start",
      plus page views and CTA clicks. days: null (all time), 7, or 30. */
  startSignups: (days) => api.get("/admin/start/signups", { params: days ? { days } : {} }).then((r) => r.data),

  /** /mugenvip services-interest submissions (Mugen referral queue). */
  structuringInterest: () => api.get("/admin/structuring-interest").then((r) => r.data),
};

/** CORE Research Layer — member-facing surface (CORE-gated). */
export const researchApi = {
  analyses: () => api.get("/research/analyses").then((r) => r.data),
  analysis: (id) => api.get(`/research/analyses/${id}`).then((r) => r.data),
  exportUrl: (id) => `${API}/research/analyses/${id}/export.pdf`,
  templateDownloadUrl: (key) => `${API}/research/templates/${key}/download.rtf`,
};

export const meApi = {
  /** Dismiss the SOLO to EXPLORE upgrade banner for this account. */
  dismissSoloBanner: () => api.post("/me/solo-banner/dismiss").then((r) => r.data),
  /** Permanently end the CORE Research first-visit callout for this account. */
  dismissResearchCallout: () => api.post("/me/research-callout/dismiss").then((r) => r.data),
};

export const reviewsApi = {  list: () => api.get("/reviews").then((r) => r.data),
  submit: (payload) => api.post("/review-submissions", payload).then((r) => r.data),
};

/** Beta 3.0 founding access: application, Rapid Diagnostic, Builder Summary. */
export const beta3Api = {
  apply: (data) => api.post("/beta3/apply", data).then((r) => r.data),
  diagnostic: () => api.get("/beta3/diagnostic").then((r) => r.data),
  saveProgress: (answers) => api.put("/beta3/diagnostic", { answers }).then((r) => r.data),
  complete: (answers) => api.post("/beta3/diagnostic/complete", { answers }).then((r) => r.data),
  summary: (id) => api.get(`/summary/${id}`).then((r) => r.data),
  feedback: (token, message) => api.post("/beta3/feedback", { token, message }).then((r) => r.data),
};

/** Advisory Circle (Beta 2.0): invitation activation + two-stage feedback. */
export const testerApi = {
  invitePreview: (token) => api.get(`/tester-invite/${token}`).then((r) => r.data),
  activate: (token, password) =>
    api.post(`/tester-invite/${token}/activate`, { password: password || null }).then((r) => r.data),
  submitFeedback: (payload) => api.post("/tester-feedback", payload).then((r) => r.data),
};

/** Monthly Clarity-to-Strategy Intelligence: CORE member benefit, admin-authored. */
export const briefingsApi = {
  list: () => api.get("/briefings").then((r) => r.data),
  adminList: () => api.get("/admin/briefings").then((r) => r.data),
  create: (data) => api.post("/admin/briefings", data).then((r) => r.data),
  update: (id, data) => api.put(`/admin/briefings/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/admin/briefings/${id}`),
};

/** CLARITY CORE program: public seat state, waitlist, member workspace. */
export const coreApi = {
  seats: () => api.get("/core/seats").then((r) => r.data),
  waitlistJoin: (data) => api.post("/core/waitlist", data).then((r) => r.data),
  inviteInfo: (token) => api.get(`/core/invite/${token}`).then((r) => r.data),
  memberDashboard: () => api.get("/core/member/dashboard").then((r) => r.data),
  briefingRequest: (data) => api.post("/core/member/briefing-request", data).then((r) => r.data),
};
